import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { Spinner } from './components/Spinner';
import { homePathForRole } from './lib/routes';
import type { ReactNode } from 'react';
import type { Role } from '../shared/types';

import { LoginScreen } from './screens/auth/LoginScreen';
import { SignupScreen } from './screens/auth/SignupScreen';
import { PendingScreen } from './screens/auth/PendingScreen';
import { ChangePasswordScreen } from './screens/auth/ChangePasswordScreen';
import { ReferralListScreen } from './screens/plumber/ReferralListScreen';
import { NewReferralScreen } from './screens/plumber/NewReferralScreen';
import { PlumberReferralScreen } from './screens/plumber/PlumberReferralScreen';
import { QueueScreen } from './screens/dispatch/QueueScreen';
import { DispatchReferralScreen } from './screens/dispatch/DispatchReferralScreen';
import { UsersScreen } from './screens/dispatch/UsersScreen';
import { TeamsScreen } from './screens/dispatch/TeamsScreen';
import { JobListScreen } from './screens/field/JobListScreen';
import { JobDetailScreen } from './screens/field/JobDetailScreen';

function Protected({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.status === 'pending') return <Navigate to="/pending" replace />;
  if (user.mustChangePassword) return <Navigate to="/change-password" replace />;
  if (!roles.includes(user.role)) return <Navigate to={homePathForRole(user)} replace />;
  return <>{children}</>;
}

function Landing() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  return <Navigate to={user ? homePathForRole(user) : '/login'} replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/signup" element={<SignupScreen />} />
          <Route path="/pending" element={<PendingScreen />} />
          <Route path="/change-password" element={<ChangePasswordScreen />} />

          <Route
            path="/referrals"
            element={
              <Protected roles={['plumber']}>
                <ReferralListScreen />
              </Protected>
            }
          />
          <Route
            path="/referrals/new"
            element={
              <Protected roles={['plumber']}>
                <NewReferralScreen />
              </Protected>
            }
          />
          <Route
            path="/referrals/:id"
            element={
              <Protected roles={['plumber']}>
                <PlumberReferralScreen />
              </Protected>
            }
          />

          <Route
            path="/dispatch"
            element={
              <Protected roles={['dispatch']}>
                <QueueScreen />
              </Protected>
            }
          />
          <Route
            path="/dispatch/referrals/:id"
            element={
              <Protected roles={['dispatch']}>
                <DispatchReferralScreen />
              </Protected>
            }
          />
          <Route
            path="/dispatch/users"
            element={
              <Protected roles={['dispatch']}>
                <UsersScreen />
              </Protected>
            }
          />
          <Route
            path="/dispatch/teams"
            element={
              <Protected roles={['dispatch']}>
                <TeamsScreen />
              </Protected>
            }
          />

          <Route
            path="/jobs"
            element={
              <Protected roles={['tech', 'recon']}>
                <JobListScreen />
              </Protected>
            }
          />
          <Route
            path="/jobs/:id"
            element={
              <Protected roles={['tech', 'recon']}>
                <JobDetailScreen />
              </Protected>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
